import React, { useRef, useState, useEffect } from 'react';

const Information = () => {
  // Referenciák a különböző szekciókhoz
  const nis2ChecklistRef = useRef<HTMLDivElement>(null);
  const nis2TasksRef = useRef<HTMLDivElement>(null);
  const nis2DataRequestRef = useRef<HTMLDivElement>(null);
  const nis2TechnicalGuideRef = useRef<HTMLDivElement>(null);
  const nis2ImplementationGuideRef = useRef<HTMLDivElement>(null);
  const nis2EnergyDataRequestRef = useRef<HTMLDivElement>(null);
  
  // A "Vissza a tetejére" gomb láthatóságának kezelése
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Scroll esemény figyelése
  useEffect(() => {
    const handleScroll = () => {
      // 300px görgés után jelenik meg a gomb
      setShowScrollToTop(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Funkció a navigációhoz
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Funkció a tetejére navigáláshoz
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      {/* Hero section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-800 rounded-xl shadow-lg mb-12 p-8 text-white">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          NIS 2 Megfelelőségi Tudnivalók
        </h1>
        <p className="text-xl mb-2">
          Átfogó útmutatók és ellenőrzőlisták a NIS 2 irányelv követelményeinek teljesítéséhez
        </p>
        <p className="text-md mb-8 text-purple-200 italic">
          Az alábbi listák és útmutatók nem teljesek, a NIS2 megfelelőség komplex folyamat. Részletes felmérésért és személyre szabott tanácsadásért kérje szakértőink segítségét!
        </p>

        {/* Navigációs csempék */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mt-6">
          <button 
            onClick={() => scrollToSection(nis2ChecklistRef)}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-4 transition-all duration-300 border border-white/30 hover:shadow-lg hover:border-white/50 hover:scale-105 group"
          >
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 mb-3 rounded-full bg-purple-500/20 flex items-center justify-center transition-all duration-300 group-hover:bg-purple-500/40">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 transition-all duration-300 group-hover:text-white/90">NIS2 Megfelelőségi Checklist</h3>
              <p className="text-sm text-white/80 transition-all duration-300 group-hover:text-white/90">Szervezeti és biztonsági ellenőrzőlista</p>
            </div>
          </button>
          
          <button 
            onClick={() => scrollToSection(nis2TasksRef)}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-4 transition-all duration-300 border border-white/30 hover:shadow-lg hover:border-white/50 hover:scale-105 group"
          >
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 mb-3 rounded-full bg-purple-500/20 flex items-center justify-center transition-all duration-300 group-hover:bg-purple-500/40">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 transition-all duration-300 group-hover:text-white/90">NIS2 Megfelelőségi Feladatok</h3>
              <p className="text-sm text-white/80 transition-all duration-300 group-hover:text-white/90">Tanácsadói és ügyfél feladatok</p>
            </div>
          </button>
          
          <button 
            onClick={() => scrollToSection(nis2DataRequestRef)}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-4 transition-all duration-300 border border-white/30 hover:shadow-lg hover:border-white/50 hover:scale-105 group"
          >
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 mb-3 rounded-full bg-purple-500/20 flex items-center justify-center transition-all duration-300 group-hover:bg-purple-500/40">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 transition-all duration-300 group-hover:text-white/90">NIS2 Adatbekérés</h3>
              <p className="text-sm text-white/80 transition-all duration-300 group-hover:text-white/90">Domain regisztrátorok és hálózati termékek</p>
            </div>
          </button>
          
          <button 
            onClick={() => scrollToSection(nis2EnergyDataRequestRef)}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-4 transition-all duration-300 border border-white/30 hover:shadow-lg hover:border-white/50 hover:scale-105 group"
          >
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 mb-3 rounded-full bg-purple-500/20 flex items-center justify-center transition-all duration-300 group-hover:bg-purple-500/40">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 transition-all duration-300 group-hover:text-white/90">NIS2 Energetikai Adatbekérés</h3>
              <p className="text-sm text-white/80 transition-all duration-300 group-hover:text-white/90">Áram/földgáz viszonteladók</p>
            </div>
          </button>
          
          <button 
            onClick={() => scrollToSection(nis2TechnicalGuideRef)}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-4 transition-all duration-300 border border-white/30 hover:shadow-lg hover:border-white/50 hover:scale-105 group"
          >
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 mb-3 rounded-full bg-purple-500/20 flex items-center justify-center transition-all duration-300 group-hover:bg-purple-500/40">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 transition-all duration-300 group-hover:text-white/90">NIS2 Technikai Útmutató</h3>
              <p className="text-sm text-white/80 transition-all duration-300 group-hover:text-white/90">Technikai megfeleltetési útmutató</p>
            </div>
          </button>
          
          <button 
            onClick={() => scrollToSection(nis2ImplementationGuideRef)}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-4 transition-all duration-300 border border-white/30 hover:shadow-lg hover:border-white/50 hover:scale-105 group"
          >
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 mb-3 rounded-full bg-purple-500/20 flex items-center justify-center transition-all duration-300 group-hover:bg-purple-500/40">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 transition-all duration-300 group-hover:text-white/90">Implementációs Útmutató</h3>
              <p className="text-sm text-white/80 transition-all duration-300 group-hover:text-white/90">Gyakorlati megvalósítási lépések</p>
            </div>
          </button>
        </div>
      </div>

      {/* NIS2 Megfelelőségi Checklist */}
      <div ref={nis2ChecklistRef} className="bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl shadow-lg p-8 border border-purple-200 mb-12">
        <h2 className="text-2xl font-bold mb-6 text-purple-900">NIS2 Megfelelőségi Checklist</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Bal oszlop - Szervezeti checklist */}
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Alapvető szervezeti információk
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Szervezet neve, címe, elérhetőségei</li>
                <li>Fő tevékenységi területek és szolgáltatások</li>
                <li>Szervezeti struktúra és felelősségi körök</li>
                <li>Alkalmazottak száma és eloszlása</li>
                <li>IT infrastruktúra áttekintése</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Kockázatkezelési intézkedések
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Kockázatértékelési módszertan és gyakoriság</li>
                <li>Azonosított kockázatok nyilvántartása</li>
                <li>Kockázatkezelési stratégiák és intézkedések</li>
                <li>Kockázati étvágy meghatározása</li>
                <li>Kockázatkezelési jelentések és felülvizsgálatok</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Alapvető biztonsági intézkedések
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Hálózat- és rendszerbiztonság</li>
                <li>Hálózati határvédelem (tűzfalak, IDS/IPS)</li>
                <li>Hozzáférés-kezelés és azonosítás</li>
                <li>Rendszerhardening és konfiguráció-menedzsment</li>
                <li>Végpontvédelem és malware elleni védelem</li>
                <li>Adattitkosítás és kulcskezelés</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Fizikai biztonság
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Fizikai hozzáférés-védelem</li>
                <li>Környezeti védelem (tűz, víz, áram)</li>
                <li>Eszközök és adathordozók kezelése</li>
                <li>Fizikai biztonsági incidensek kezelése</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Ellátási lánc biztonság
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Beszállítók biztonsági értékelése</li>
                <li>Harmadik felek hozzáférés-kezelése</li>
                <li>Szerződéses biztonsági követelmények</li>
                <li>Beszállítói audit és megfelelőség-ellenőrzés</li>
              </ul>
            </div>
          </div>
          
          {/* Jobb oszlop - További checklist elemek */}
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Üzletmenet-folytonosság és incidens-kezelés
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Üzletmenet-folytonossági tervek</li>
                <li>Katasztrófa-helyreállítási tervek</li>
                <li>Incidens-felismerési és -jelentési eljárások</li>
                <li>Incidens-kezelési folyamatok és felelősségek</li>
                <li>Tesztelés és gyakorlatok</li>
                <li>Hatóságok felé történő jelentési kötelezettségek</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Biztonsági szabályzatok és eljárások
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Információbiztonsági szabályzat</li>
                <li>Adatvédelmi szabályzat</li>
                <li>Hozzáférés-kezelési szabályzat</li>
                <li>Változáskezelési eljárások</li>
                <li>Biztonsági tudatossági program</li>
                <li>Megfelelőség-ellenőrzési eljárások</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                NIS2 Technikai Megfeleltetési Útmutató
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Technikai megfelelőségi követelmények</li>
                <li>Biztonsági architektúra tervezése</li>
                <li>Védelmi rétegek kialakítása</li>
                <li>Monitorozási és naplózási rendszerek</li>
                <li>Sérülékenységkezelés és javítás</li>
                <li>Biztonsági tesztelés és értékelés</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Tanácsadói feladatok
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Előzetes felmérés és gap analízis</li>
                <li>Adatgyűjtés és elemzés</li>
                <li>Kockázatértékelés</li>
                <li>Intézkedési terv kidolgozása</li>
                <li>Implementáció támogatása</li>
                <li>Oktatás és tudatosítás</li>
                <li>Megfelelőség ellenőrzése és dokumentálása</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Ügyfél feladatai
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Adatszolgáltatás és információ biztosítása</li>
                <li>Erőforrások allokálása</li>
                <li>Belső koordináció</li>
                <li>Javasolt intézkedések implementálása</li>
                <li>Dokumentáció elkészítése és karbantartása</li>
                <li>Folyamatos megfelelőség biztosítása</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* NIS2 Technikai Megfeleltetési Útmutató */}
      <div ref={nis2TechnicalGuideRef} className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl shadow-lg p-8 border border-indigo-200 mb-12">
        <h2 className="text-2xl font-bold mb-6 text-indigo-900">NIS2 Technikai Megfeleltetési Útmutató</h2>
        
        <div className="mb-6">
          <p className="text-gray-700 mb-4">
            A NIS2 technikai megfeleltetés komplex folyamat, amely mind a tanácsadói oldalról, mind az ügyfél részéről aktív közreműködést igényel. Az alábbiakban összefoglaljuk a főbb feladatokat mindkét fél számára.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Bal oszlop - Tanácsadói feladatok */}
          <div>
            <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-indigo-300 pb-2">
              Tanácsadói feladatok
            </h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Előzetes felmérés és gap analízis</li>
              <li>Adatgyűjtés és elemzés</li>
              <li>Kockázatértékelés</li>
              <li>Intézkedési terv kidolgozása</li>
              <li>Implementáció támogatása</li>
              <li>Oktatás és tudatosítás</li>
              <li>Megfelelőség ellenőrzése és dokumentálása</li>
            </ul>
          </div>
          
          {/* Jobb oszlop - Ügyfél feladatai */}
          <div>
            <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-indigo-300 pb-2">
              Ügyfél feladatai
            </h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Adatszolgáltatás és információ biztosítása</li>
              <li>Erőforrások allokálása</li>
              <li>Belső koordináció</li>
              <li>Javasolt intézkedések implementálása</li>
              <li>Dokumentáció elkészítése és karbantartása</li>
              <li>Folyamatos megfelelőség biztosítása</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 mb-6">
          <p className="text-indigo-700">
            <strong>Fontos:</strong> A sikeres NIS2 megfelelőség kulcsa a tanácsadó és az ügyfél közötti szoros együttműködés. A folyamat során rendszeres egyeztetésekre és visszajelzésekre van szükség a hatékony implementáció érdekében.
          </p>
        </div>
        
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-indigo-800 mb-4">Technikai megfelelőségi területek</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-medium text-indigo-700 mb-2">Hálózatbiztonság</h4>
              <p className="text-sm text-gray-600">Tűzfalak, IDS/IPS, szegmentáció, VPN</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-medium text-indigo-700 mb-2">Végpontbiztonság</h4>
              <p className="text-sm text-gray-600">EDR, antivírus, patch management</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-medium text-indigo-700 mb-2">Adatvédelem</h4>
              <p className="text-sm text-gray-600">Titkosítás, DLP, adatszivárgás megelőzés</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-medium text-indigo-700 mb-2">Hozzáférés-kezelés</h4>
              <p className="text-sm text-gray-600">IAM, MFA, jogosultságkezelés</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-medium text-indigo-700 mb-2">Naplózás és monitorozás</h4>
              <p className="text-sm text-gray-600">SIEM, log management, anomália detektálás</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-medium text-indigo-700 mb-2">Incidenskezelés</h4>
              <p className="text-sm text-gray-600">Detektálás, reagálás, jelentés, helyreállítás</p>
            </div>
          </div>
        </div>
      </div>

      {/* Technikai NIS2 Checklist */}
      <div ref={nis2DataRequestRef} className="bg-gradient-to-br from-green-100 to-blue-100 rounded-xl shadow-lg p-8 border border-green-200 mb-12">
        <h2 className="text-2xl font-bold mb-6 text-green-900">NIS2 Adatbekérési Lista Domain Regisztrátorok és Hálózati Termékekkel Foglalkozó Cégek Számára</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Bal oszlop */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                1. Alapadatok
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Cégnév, cégjegyzékszám, adószám</li>
                <li>Szervezeti felépítés (organogram) és felelősségi körök</li>
                <li>Alkalmazotti létszám és szerepkörök (különös tekintettel az informatikai és biztonsági személyzetre)</li>
                <li>Éves árbevétel az elmúlt 3 évben</li>
                <li>A cég szolgáltatásainak és termékeinek teljes listája</li>
                <li>Kiszolgált ügyfelek típusai és szektorai (kritikus infrastruktúrára vonatkozó ügyféllista)</li>
                <li>Nemzetközi jelenlét (külföldi tevékenységek, leányvállalatok)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                2. Domain regisztrációs tevékenységhez kapcsolódó adatok
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Akkreditációk és jogosultságok a különböző TLD-khez (Top Level Domain)</li>
                <li>Regisztrált és kezelt domain-nevek teljes száma</li>
                <li>Regisztrált domain-ek kategorizálása TLD szerint (.hu, .com, .eu, stb.)</li>
                <li>Domain regisztrációs platform technikai dokumentációja</li>
                <li>Domain regisztrációs platform biztonsági auditjának eredményei</li>
                <li>Automatizált domain kezelési folyamatok dokumentációja</li>
                <li>Domain átruházási és kezelési folyamatok leírása</li>
                <li>Domain regisztrátorokkal való kapcsolattartás folyamatai</li>
                <li>Domain regisztrációs adatok tárolási módja és helye</li>
                <li>Domain névszerverek listája és konfigurációja</li>
                <li>WHOIS adatvédelmi megfelelőség bizonyítéka</li>
                <li>Domain regisztrációs szolgáltatás SLA mutatói és biztonsági garanciái</li>
                <li>Domain lejárati értesítési rendszer működése</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                3. Hálózati termékekhez kapcsolódó adatok
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Teljes termékportfólió részletes listája kategóriánként (routerek, switchek, tűzfalak, stb.)</li>
                <li>Beszállítói kapcsolatok listája és szerződései</li>
                <li>Hardware és firmware frissítési folyamatok dokumentációja</li>
                <li>Termékekhez kapcsolódó biztonsági tanúsítványok és megfelelőségek</li>
                <li>Termékekkel kapcsolatos ismert sérülékenységek és kezelésük</li>
                <li>Termék-életciklus kezelési dokumentáció</li>
                <li>Termékekhez kapcsolódó biztonsági tesztelések eredményei</li>
                <li>EOL (End-of-Life) és EOS (End-of-Support) kezelési stratégia</li>
                <li>Termékekhez kapcsolódó biztonsági incidensek nyilvántartása</li>
                <li>Szoftverkomponensek listája (Software Bill of Materials - SBOM)</li>
                <li>Termékdokumentációk biztonsági részei</li>
                <li>Firmware aláírási és hitelesítési eljárások</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                4. Infrastruktúra és rendszerek
              </h3>
              
              <div className="ml-4 mb-4">
                <h4 className="text-lg font-medium text-green-700 mb-3">4.1 Saját IT infrastruktúra</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>IT rendszerek leltára (szerverek, munkaállomások, hálózati eszközök)</li>
                  <li>Hálózati topológia részletes diagramja</li>
                  <li>Adatközpontok és szerverhelységek listája és biztonsági jellemzői</li>
                  <li>Virtualizációs infrastruktúra részletei</li>
                  <li>Felhőszolgáltatások használati adatai</li>
                  <li>Belső hálózati szegmentáció dokumentációja</li>
                  <li>Távoli munkavégzés infrastruktúrája</li>
                  <li>Infrastruktúra redundancia és magas rendelkezésre állási megoldások</li>
                  <li>Használt operációs rendszerek és verziószámok</li>
                  <li>Biztonsági szoftverek listája és konfigurációja</li>
                </ul>
              </div>
              
              <div className="ml-4">
                <h4 className="text-lg font-medium text-green-700 mb-3">4.2 Szolgáltatási infrastruktúra</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Domain regisztrációs és kezelési rendszerek architektúrája</li>
                  <li>Végfelhasználói felületek (webportálok) technikai részletei</li>
                  <li>API-k dokumentációja és biztonsági kontrolljai</li>
                  <li>Ügyféladatok tárolási rendszerei</li>
                  <li>DNS szolgáltatás infrastruktúrája (ha van ilyen)</li>
                  <li>Hosting szolgáltatás infrastruktúrája (ha van ilyen)</li>
                  <li>Email szolgáltatás infrastruktúrája (ha van ilyen)</li>
                  <li>Adatbázisrendszerek és konfigurációjuk</li>
                  <li>Terheléselosztás és skálázási megoldások</li>
                  <li>Monitoring rendszerek és azok konfigurációja</li>
                </ul>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                5. Adatkezelés és adatvédelem
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Kezelt adatok teljes leltára és kategorizálása</li>
                <li>Adatkezelési tevékenységek nyilvántartása (GDPR 30. cikk)</li>
                <li>Adatkezelési tájékoztatók és szabályzatok</li>
                <li>Adatfeldolgozói és közös adatkezelői szerződések</li>
                <li>Adatvédelmi hatásvizsgálatok (DPIA) eredményei</li>
                <li>Adattárolási és adatmegőrzési politikák</li>
                <li>Adathordozhatósági és törlési kérelmek kezelési folyamata</li>
                <li>Domain regisztrációkhoz kapcsolódó személyes adatok kezelési módja</li>
                <li>WHOIS adatvédelmi korlátozások kezelése</li>
                <li>Harmadik országba történő adattovábbítások részletei</li>
                <li>Cookie-k és nyomkövetési technológiák használata</li>
                <li>Adatvédelmi incidensek nyilvántartása</li>
                <li>Okos mérőkből származó adatok kezelésének speciális szabályai</li>
                <li>Negyedórás/órás fogyasztási adatok adatvédelmi vonatkozásai</li>
                <li>Háztartási fogyasztási minták anonimizálási módszerei</li>
                <li>Fogyasztói szokások elemzésének jogi alapja és korlátozásai</li>
                <li>Energiahatékonysági tanácsadáshoz kapcsolódó adatkezelés</li>
                <li>Különleges adatok kezelése (pl. védendő fogyasztók)</li>
                <li>Automatizált döntéshozatal és profilalkotás jogalapja és korlátai</li>
                <li>Fogyasztók adathozzáférési jogainak biztosítása</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                6. Hozzáférés-kezelés és azonosítás
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>IAM (Identity and Access Management) rendszer dokumentációja</li>
                <li>Privilegizált hozzáférések kezelési módja</li>
                <li>Felhasználói jogosultság-felülvizsgálati eljárások</li>
                <li>Többfaktoros hitelesítés (MFA) implementálási státusza</li>
                <li>Távoli hozzáférés biztonsági kontrollja</li>
                <li>Jelszókezelési politikák</li>
                <li>Szolgáltatói és partneri hozzáférések</li>
                <li>Domain adminisztrátori hozzáférések kezelése</li>
                <li>Regisztrátori felületek hozzáférés-vezérlése</li>
                <li>API kulcsok és tokenek kezelése</li>
                <li>Domain regisztrációs rendszer privilegizált műveleteinek naplózása</li>
                <li>Ügyfél-hozzáférések kezelési módszerei</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                7. Incidenskezelés és üzletmenet-folytonosság
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Incidenskezelési eljárás dokumentációja</li>
                <li>Biztonsági incidensek nyilvántartása az elmúlt 2 évből</li>
                <li>Incidensjelentési és eszkalációs útvonalak</li>
                <li>Incident response team összetétele és képzettsége</li>
                <li>Válaszidők és helyreállítási célok (RTO/RPO)</li>
                <li>NIS2 szerinti incidensjelentési eljárások</li>
                <li>Domain visszaélésekre (abuse) vonatkozó eljárások</li>
                <li>Üzletmenet-folytonossági terv (BCP)</li>
                <li>Katasztrófa utáni helyreállítási terv (DRP)</li>
                <li>Kritikus rendszerek és szolgáltatások listája</li>
                <li>BCP/DRP tesztelési eredmények</li>
                <li>Domain szolgáltatás folytonossági intézkedések</li>
                <li>Registry-lock és egyéb domain védelmi mechanizmusok</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                8. Biztonsági szabályzatok és eljárások
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Információbiztonsági szabályzat (IBSZ)</li>
                <li>Információbiztonsági irányítási rendszer (ISMS) dokumentáció</li>
                <li>Kockázatkezelési módszertan és mátrix</li>
                <li>Kockázatelemzési jelentések</li>
                <li>Belső audit jelentések</li>
                <li>Külső auditok és tanúsítások (ISO 27001, stb.)</li>
                <li>Változáskezelési eljárások</li>
                <li>Fejlesztési és üzemeltetési szabályzatok</li>
                <li>Patch management eljárások</li>
                <li>Domain regisztrációval kapcsolatos biztonsági szabályzatok</li>
                <li>Beszállítói biztonsági követelmények</li>
                <li>Távoli munkavégzés szabályai</li>
              </ul>
            </div>
          </div>
          
          {/* Jobb oszlop */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                9. Ellátási lánc biztonság
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Kritikus beszállítók és partnerek listája</li>
                <li>Beszállítói szerződések biztonsági záradékai</li>
                <li>Beszállítói értékelési és auditálási folyamatok</li>
                <li>Registry operátorokkal kötött szerződések</li>
                <li>Hardver és szoftver beszerzési folyamatok</li>
                <li>Hardverek és termékek eredetiségének ellenőrzési módszerei</li>
                <li>Supply chain attack megelőzési intézkedések</li>
                <li>Beszállítói incidensjelentési követelmények</li>
                <li>Harmadik fél által biztosított szolgáltatások biztonsági értékelése</li>
                <li>Kiszervezett tevékenységek listája és ellenőrzési módszerei</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                10. Technikai biztonsági kontrollok
              </h3>
              
              <div className="ml-4 mb-4">
                <h4 className="text-lg font-medium text-green-700 mb-3">10.1 Hálózatbiztonság</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Tűzfalszabályok és -konfigurációk</li>
                  <li>IDS/IPS rendszerek és konfigurációjuk</li>
                  <li>Hálózati szegmentáció implementációja</li>
                  <li>DMZ architektúra</li>
                  <li>VPN megoldások</li>
                  <li>SSL/TLS beállítások és tanúsítványkezelés</li>
                  <li>DDoS védelem megoldásai</li>
                  <li>DNS biztonsági megoldások (DNSSEC, DNS over HTTPS)</li>
                  <li>Hálózati forgalom monitorozás</li>
                  <li>Vezeték nélküli hálózatok biztonsága</li>
                </ul>
              </div>
              
              <div className="ml-4 mb-4">
                <h4 className="text-lg font-medium text-green-700 mb-3">10.2 Végpontbiztonság</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Végpontvédelmi megoldások</li>
                  <li>Malware védelem</li>
                  <li>Endpoint detection and response (EDR) megoldások</li>
                  <li>Mobil eszköz menedzsment (MDM)</li>
                  <li>Adatszivárgás megelőzés (DLP)</li>
                  <li>Eszköztitkosítás implementációja</li>
                  <li>USB és hordozható média korlátozások</li>
                  <li>Patch management megoldások</li>
                  <li>Végpont hardening eljárások</li>
                  <li>BYOD szabályzatok és kontrollok</li>
                </ul>
              </div>
              
              <div className="ml-4">
                <h4 className="text-lg font-medium text-green-700 mb-3">10.3 Alkalmazásbiztonság</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Domain regisztrációs rendszer biztonsági tesztelési jelentései</li>
                  <li>Webalkalmazás tűzfalak (WAF) konfigurációja</li>
                  <li>Kód biztonsági ellenőrzések (SAST/DAST)</li>
                  <li>API biztonság</li>
                  <li>Inputvalidáció és output encoding</li>
                  <li>Autentikációs mechanizmusok</li>
                  <li>Session kezelés biztonsága</li>
                  <li>Password hashing és tárolás</li>
                  <li>SQL injection védelem</li>
                  <li>XSS és CSRF védelem</li>
                </ul>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                11. Monitoring és naplózás
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Naplógyűjtési infrastruktúra</li>
                <li>Naplóelemzési képességek</li>
                <li>Biztonsági eseménymonitorozás (SIEM)</li>
                <li>Anomáliadetektálás</li>
                <li>Riasztási szabályok és küszöbértékek</li>
                <li>Domain regisztrációs tevékenységek naplózása</li>
                <li>Adminisztratív tevékenységek auditálása</li>
                <li>Naplómegőrzési politikák</li>
                <li>Naplóarchiválási módszerek</li>
                <li>Naplóintegritás védelem</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                12. Sérülékenységkezelés és patch management
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Sérülékenységkezelési folyamat dokumentációja</li>
                <li>Rendszeres sérülékenységvizsgálatok eredményei</li>
                <li>Penetrációs tesztek eredményei</li>
                <li>Javítási (remediation) folyamatok és időkeretek</li>
                <li>Patch management eljárások és ütemezés</li>
                <li>0-day sérülékenységek kezelési protokollja</li>
                <li>Biztonsági közlemények követésének módszere</li>
                <li>Sérülékenységek nyomon követése és jelentése</li>
                <li>Fenyegetettség-intelligencia források és felhasználásuk</li>
                <li>Domain regisztrációs rendszer frissítési eljárásai</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                13. Emberi tényezők és képzés
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Információbiztonsági tudatossági program</li>
                <li>Biztonsági képzések nyilvántartása</li>
                <li>Munkaerő-felvételi biztonsági ellenőrzések</li>
                <li>Kiléptetési eljárások</li>
                <li>Titoktartási megállapodások (NDA)</li>
                <li>Szerepkör-specifikus biztonsági képzések</li>
                <li>Phishing szimulációk és eredményeik</li>
                <li>Biztonsági incidensek jelentésének oktatása</li>
                <li>Social engineering tudatosság</li>
                <li>Domain regisztrációs visszaélésekkel kapcsolatos tudatosság</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                14. Külső kapcsolatok és megfelelőség
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Hatóságokkal való kapcsolattartás rendje</li>
                <li>Iparági szövetségekben való részvétel</li>
                <li>Jogszabályi megfelelőségi értékelések</li>
                <li>Szerződéses kötelezettségek listája</li>
                <li>Biztosítási fedezetek (kiberbiztonság, felelősségbiztosítás)</li>
                <li>Adatvédelmi hatósággal való együttműködés protokollja</li>
                <li>Egyéb szabályozói kapcsolattartás</li>
                <li>Iparági szabványoknak való megfelelés bizonyítéka</li>
                <li>Audit jelentések külső ellenőrzésekről</li>
                <li>Registry-kkel való kapcsolattartás protokollja</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                15. Ágazatspecifikus biztonsági kontrollok
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Domain abuse monitoring és jelentési rendszer</li>
                <li>Domain hijacking elleni védelem</li>
                <li>Domain-szintű biztonsági funkciók (DNSSEC, CAA, DMARC, SPF, DKIM)</li>
                <li>Registry lock implementációja</li>
                <li>Tömeges domain regisztrációk monitorozása</li>
                <li>Domain typosquatting és cybersquatting észlelés</li>
                <li>Domain expiration kezelés</li>
                <li>Törölt domain-ek visszaállítási eljárása</li>
                <li>Zónafájl-integritás ellenőrzés</li>
                <li>DNS anomália-detektálás</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-4 border-b border-green-300 pb-2">
                16. Technikai dokumentáció
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Hálózati diagramok</li>
                <li>Adatfolyam-diagramok</li>
                <li>Rendszerarchitektúra dokumentáció</li>
                <li>Biztonsági architektúra dokumentáció</li>
                <li>Helyreállítási dokumentáció</li>
                <li>API dokumentáció</li>
                <li>Integrációs dokumentáció</li>
                <li>Üzemeltetési kézikönyvek</li>
                <li>Szoftverkomponensek listája (SBOM)</li>
                <li>Konfigurációs alapbeállítások (baseline) dokumentációja</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div ref={nis2EnergyDataRequestRef} className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl shadow-lg p-8 border border-amber-200 mb-12">
        <h2 className="text-2xl font-bold mb-6 text-amber-900">NIS2 Adatbekérési Lista Energetikai Viszonteladók Számára</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Bal oszlop */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                1. Alapadatok
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Cégnév, cégjegyzékszám, adószám</li>
                <li>Szervezeti felépítés (organogram) és felelősségi körök</li>
                <li>Alkalmazotti létszám és szerepkörök (különös tekintettel az informatikai és biztonsági személyzetre)</li>
                <li>Éves árbevétel az elmúlt 3 évben</li>
                <li>A cég szolgáltatásainak és termékeinek teljes listája</li>
                <li>Kiszolgált ügyfelek típusai és szektorai</li>
                <li>Viszonteladott energiatermékek típusa (áram, földgáz, mindkettő)</li>
                <li>Nemzetközi jelenlét (külföldi tevékenységek, leányvállalatok)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                2. Energiaszolgáltatási tevékenységhez kapcsolódó adatok
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Engedélyek és jogosultságok a viszonteladói tevékenységhez</li>
                <li>Szerződött ügyfelek teljes száma (szegmentálva lakossági/üzleti/ipari kategóriák szerint)</li>
                <li>Szerződött árammennyiség (MWh) és/vagy földgázmennyiség (m³) az elmúlt évben</li>
                <li>Energia-kereskedési platformok technikai dokumentációja</li>
                <li>Energia-kereskedési platformok biztonsági auditjainak eredményei</li>
                <li>Automatizált számlázási és mérési adatkezelési folyamatok dokumentációja</li>
                <li>Ügyfélszerződés kezelési folyamatok leírása</li>
                <li>Nagykereskedőkkel, termelőkkel és elosztóhálózati engedélyesekkel való kapcsolattartás protokollja</li>
                <li>Mérési adatok tárolási módja és helye</li>
                <li>Energia-kereskedelmi rendszerek és interfészek listája</li>
                <li>Adatvédelmi megfelelőség bizonyítéka az energiafogyasztási adatokra vonatkozóan</li>
                <li>Energiaszolgáltatási SLA mutatók és biztonsági garanciák</li>
                <li>Energiamérési adatok anomália-detektálása és validálási folyamatai</li>
                <li>Mérlegköri tagság és elszámolási dokumentációk</li>
                <li>Energiapiaci szabályozó hatósággal való együttműködés dokumentumai</li>
                <li>Szerződésállomány-kezelési rendszer biztonsági dokumentációja</li>
                <li>Okos mérési rendszerekkel való integráció leírása és biztonsági kontrolljai</li>
                <li>Energiaár-előrejelző és kockázatkezelő rendszerek dokumentációja</li>
                <li>Fogyasztási profilok kezelésének módszertana</li>
                <li>Kiegyenlítő energia elszámolási mechanizmusok</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                3. Energiakereskedelmi rendszerekhez kapcsolódó adatok
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Energiakereskedési szoftverek részletes listája</li>
                <li>IT beszállítói kapcsolatok listája és szerződései</li>
                <li>Szoftver és firmware frissítési folyamatok dokumentációja</li>
                <li>Kereskedelmi rendszerekhez kapcsolódó biztonsági tanúsítványok és megfelelőségek</li>
                <li>Energiakereskedési rendszerek ismert sérülékenységei és kezelésük</li>
                <li>Rendszer-életciklus kezelési dokumentáció</li>
                <li>Kereskedési és elszámolási rendszerek biztonsági tesztelésének eredményei</li>
                <li>EOL (End-of-Life) és EOS (End-of-Support) kezelési stratégia</li>
                <li>Rendszerekhez kapcsolódó biztonsági incidensek nyilvántartása</li>
                <li>Szoftverkomponensek listája (Software Bill of Materials - SBOM)</li>
                <li>Rendszerdokumentációk biztonsági részei</li>
                <li>Hozzáférés-menedzsment az energiakereskedési platformokhoz</li>
                <li>Adatkommunikációs protokollok a rendszerüzemeltetőkkel</li>
                <li>Energiakereskedési algoritmusok biztonsági értékelése</li>
                <li>Integrált rendszerek közötti adatáramlás biztonsági kontrollja</li>
                <li>Határkeresztező kapacitáskezelő rendszerek dokumentációja (ha releváns)</li>
                <li>Másnapi/napon belüli kereskedési rendszerek biztonsági dokumentációja</li>
                <li>Energiatőzsdei rendszerekkel való kapcsolódási pontok biztonsága</li>
                <li>Kereskedési automatizációk és API-k biztonsági vizsgálata</li>
                <li>Backup és helyreállítási eljárások a kereskedési rendszerekhez</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                4. Infrastruktúra és rendszerek
              </h3>
              
              <div className="ml-4 mb-4">
                <h4 className="text-lg font-medium text-amber-700 mb-3">4.1 Saját IT infrastruktúra</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>IT rendszerek leltára (szerverek, munkaállomások, hálózati eszközök)</li>
                  <li>Hálózati topológia részletes diagramja</li>
                  <li>Adatközpontok és szerverhelységek listája és biztonsági jellemzői</li>
                  <li>Virtualizációs infrastruktúra részletei</li>
                  <li>Felhőszolgáltatások használati adatai</li>
                  <li>Belső hálózati szegmentáció dokumentációja</li>
                  <li>Távoli munkavégzés infrastruktúrája</li>
                  <li>Infrastruktúra redundancia és magas rendelkezésre állási megoldások</li>
                  <li>Használt operációs rendszerek és verziószámok</li>
                  <li>Biztonsági szoftverek listája és konfigurációja</li>
                </ul>
              </div>
              
              <div className="ml-4">
                <h4 className="text-lg font-medium text-amber-700 mb-3">4.2 Szolgáltatási infrastruktúra</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Energiakereskedési és elszámolási rendszerek architektúrája</li>
                  <li>Ügyfélportálok technikai részletei</li>
                  <li>API-k dokumentációja és biztonsági kontrolljai</li>
                  <li>Ügyféladatok tárolási rendszerei</li>
                  <li>Mérési adatgyűjtő és feldolgozó infrastruktúra</li>
                  <li>Számlázási rendszer infrastruktúrája</li>
                  <li>Email kommunikációs infrastruktúra</li>
                  <li>Adatbázisrendszerek és konfigurációjuk</li>
                  <li>Terheléselosztás és skálázási megoldások</li>
                  <li>Monitoring rendszerek és azok konfigurációja</li>
                </ul>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                5. Adatkezelés és adatvédelem
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Kezelt adatok teljes leltára és kategorizálása</li>
                <li>Adatkezelési tevékenységek nyilvántartása (GDPR 30. cikk)</li>
                <li>Adatkezelési tájékoztatók és szabályzatok</li>
                <li>Adatfeldolgozói és közös adatkezelői szerződések</li>
                <li>Adatvédelmi hatásvizsgálatok (DPIA) eredményei</li>
                <li>Adattárolási és adatmegőrzési politikák</li>
                <li>Adathordozhatósági és törlési kérelmek kezelési folyamata</li>
                <li>Fogyasztási adatokhoz kapcsolódó személyes adatok kezelési módja</li>
                <li>Energiafogyasztási profilozás adatvédelmi vonatkozásai</li>
                <li>Harmadik országba történő adattovábbítások részletei</li>
                <li>Cookie-k és nyomkövetési technológiák használata</li>
                <li>Adatvédelmi incidensek nyilvántartása</li>
                <li>Okos mérőkből származó adatok kezelésének speciális szabályai</li>
                <li>Negyedórás/órás fogyasztási adatok adatvédelmi vonatkozásai</li>
                <li>Háztartási fogyasztási minták anonimizálási módszerei</li>
                <li>Fogyasztói szokások elemzésének jogi alapja és korlátozásai</li>
                <li>Energiahatékonysági tanácsadáshoz kapcsolódó adatkezelés</li>
                <li>Különleges adatok kezelése (pl. védendő fogyasztók)</li>
                <li>Automatizált döntéshozatal és profilalkotás jogalapja és korlátai</li>
                <li>Fogyasztók adathozzáférési jogainak biztosítása</li>
              </ul>
            </div>
          </div>
          
          {/* Jobb oszlop */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                6. Hozzáférés-kezelés és azonosítás
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>IAM (Identity and Access Management) rendszer dokumentációja</li>
                <li>Privilegizált hozzáférések kezelési módja</li>
                <li>Felhasználói jogosultság-felülvizsgálati eljárások</li>
                <li>Többfaktoros hitelesítés (MFA) implementálási státusza</li>
                <li>Távoli hozzáférés biztonsági kontrollja</li>
                <li>Jelszókezelési politikák</li>
                <li>Szolgáltatói és partneri hozzáférések</li>
                <li>Energia-kereskedelmi platformok adminisztrátori hozzáférése</li>
                <li>Mérési adatkezelő felületek hozzáférés-vezérlése</li>
                <li>API kulcsok és tokenek kezelése</li>
                <li>Kritikus rendszerműveletek naplózása</li>
                <li>Ügyfél-hozzáférések kezelési módszerei</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                7. Incidenskezelés és üzletmenet-folytonosság
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Incidenskezelési eljárás dokumentációja</li>
                <li>Biztonsági incidensek nyilvántartása az elmúlt 2 évből</li>
                <li>Incidensjelentési és eszkalációs útvonalak</li>
                <li>Incident response team összetétele és képzettsége</li>
                <li>Válaszidők és helyreállítási célok (RTO/RPO)</li>
                <li>NIS2 szerinti incidensjelentési eljárások</li>
                <li>Energiaszolgáltatási visszaélésekre vonatkozó eljárások</li>
                <li>Üzletmenet-folytonossági terv (BCP)</li>
                <li>Katasztrófa utáni helyreállítási terv (DRP)</li>
                <li>Kritikus rendszerek és szolgáltatások listája</li>
                <li>BCP/DRP tesztelési eredmények</li>
                <li>Energiakereskedési folytonosság-tervezés</li>
                <li>Alternatív kereskedelmi és elszámolási eljárások vészhelyzet esetén</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                8. Biztonsági szabályzatok és eljárások
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Információbiztonsági szabályzat (IBSZ)</li>
                <li>Információbiztonsági irányítási rendszer (ISMS) dokumentáció</li>
                <li>Kockázatkezelési módszertan és mátrix</li>
                <li>Kockázatelemzési jelentések</li>
                <li>Belső audit jelentések</li>
                <li>Külső auditok és tanúsítások (ISO 27001, stb.)</li>
                <li>Változáskezelési eljárások</li>
                <li>Fejlesztési és üzemeltetési szabályzatok</li>
                <li>Patch management eljárások</li>
                <li>Energiakereskedéshez kapcsolódó biztonsági szabályzatok</li>
                <li>Beszállítói biztonsági követelmények</li>
                <li>Távoli munkavégzés szabályai</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                9. Ellátási lánc biztonság
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Kritikus beszállítók és partnerek listája</li>
                <li>Beszállítói szerződések biztonsági záradékai</li>
                <li>Beszállítói értékelési és auditálási folyamatok</li>
                <li>Nagykereskedői és termelői szerződések</li>
                <li>Hardver és szoftver beszerzési folyamatok</li>
                <li>Hardverek és termékek eredetiségének ellenőrzési módszerei</li>
                <li>Supply chain attack megelőzési intézkedések</li>
                <li>Beszállítói incidensjelentési követelmények</li>
                <li>Harmadik fél által biztosított szolgáltatások biztonsági értékelése</li>
                <li>Kiszervezett tevékenységek listája és ellenőrzési módszerei</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                10. Technikai biztonsági kontrollok
              </h3>
              
              <div className="ml-4 mb-4">
                <h4 className="text-lg font-medium text-amber-700 mb-3">10.1 Hálózatbiztonság</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Tűzfalszabályok és -konfigurációk</li>
                  <li>IDS/IPS rendszerek és konfigurációjuk</li>
                  <li>Hálózati szegmentáció implementációja</li>
                  <li>DMZ architektúra</li>
                  <li>VPN megoldások</li>
                  <li>SSL/TLS beállítások és tanúsítványkezelés</li>
                  <li>DDoS védelem megoldásai</li>
                  <li>DNS biztonsági megoldások</li>
                  <li>Hálózati forgalom monitorozás</li>
                  <li>Vezeték nélküli hálózatok biztonsága</li>
                </ul>
              </div>
              
              <div className="ml-4 mb-4">
                <h4 className="text-lg font-medium text-amber-700 mb-3">10.2 Végpontbiztonság</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Végpontvédelmi megoldások</li>
                  <li>Malware védelem</li>
                  <li>Endpoint detection and response (EDR) megoldások</li>
                  <li>Mobil eszköz menedzsment (MDM)</li>
                  <li>Adatszivárgás megelőzés (DLP)</li>
                  <li>Eszköztitkosítás implementációja</li>
                  <li>USB és hordozható média korlátozások</li>
                  <li>Patch management megoldások</li>
                  <li>Végpont hardening eljárások</li>
                  <li>BYOD szabályzatok és kontrollok</li>
                </ul>
              </div>
              
              <div className="ml-4">
                <h4 className="text-lg font-medium text-amber-700 mb-3">10.3 Alkalmazásbiztonság</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Energiakereskedési rendszer biztonsági tesztelési jelentései</li>
                  <li>Webalkalmazás tűzfalak (WAF) konfigurációja</li>
                  <li>Kód biztonsági ellenőrzések (SAST/DAST)</li>
                  <li>API biztonság</li>
                  <li>Inputvalidáció és output encoding</li>
                  <li>Autentikációs mechanizmusok</li>
                  <li>Session kezelés biztonsága</li>
                  <li>Password hashing és tárolás</li>
                  <li>SQL injection védelem</li>
                  <li>XSS és CSRF védelem</li>
                </ul>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                11. Ágazatspecifikus biztonsági kontrollok
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Energiakereskedési csalások detektálása és megelőzése</li>
                <li>Mérési adatok manipulálása elleni védelem</li>
                <li>Energiakereskedelmi rendszerek biztonsági funkcionalitása</li>
                <li>Fogyasztásmérési adatok anomália-detektálása</li>
                <li>Árérzékeny kereskedelmi adatok védelme</li>
                <li>Szabályozó hatóságokkal való kommunikáció protokollja</li>
                <li>Energia-ellátási krízishelyzetek kezelése</li>
                <li>Hálózatüzemeltetőkkel való kapcsolattartás biztonsági protokollja</li>
                <li>Nagykereskedelmi platformok biztonsági kontrolljainak ismerete</li>
                <li>Mérlegkör-felelősi tevékenység biztonsági vonatkozásai (ha releváns)</li>
                <li>Energiapiaci manipulációk észlelése és megelőzése (REMIT)</li>
                <li>Bennfentes információkezelés kontrolljai és naplózása</li>
                <li>Földgáz egyensúlyozási mechanizmusok biztonsági vonatkozásai</li>
                <li>Villamos energia határkeresztező kereskedelem biztonsági aspektusai</li>
                <li>Rendszerszintű szolgáltatásokhoz kapcsolódó adatkezelés (ha releváns)</li>
                <li>Megújuló energia termelés előrejelzési rendszerek biztonsága</li>
                <li>Energiahatékonysági kötelezettségi rendszer dokumentációja</li>
                <li>Vészhelyzeti energiaellátási tervek és folyamatok</li>
                <li>Érzékeny árképzési információk védelme a konkurenciától</li>
                <li>Energiaszegénység kezeléséhez kapcsolódó programok adatvédelme</li>
                <li>Tömeges kereskedési/elszámolási folyamatok hibakezelése</li>
                <li>Árjelző rendszerek biztonsági kontrollja</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-amber-800 mb-4 border-b border-amber-300 pb-2">
                12. Szabályozási megfelelőség
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Energiapiaci liberalizációs szabályoknak való megfelelés bizonyítékai</li>
                <li>Nemzeti energiaszabályozó hatóság iránymutatásainak implementálása</li>
                <li>REMIT (1227/2011/EU) megfelelőségi dokumentáció</li>
                <li>ACER jelentéstételi kötelezettségek teljesítésének bizonyítéka</li>
                <li>Uniós hálózati kódexeknek való megfelelés (ha releváns)</li>
                <li>Energiahatékonysági kötelezettségeknek való megfelelés</li>
                <li>Megújuló energia direktíva megfelelőség (RED II, RED III)</li>
                <li>Tiszta Energia Csomag implementációs dokumentáció</li>
                <li>Árelőrejelzési kötelezettségek teljesítésének módja</li>
                <li>Egyetemes szolgáltatási/végső menedékes kötelezettségek (ha van)</li>
                <li>Fogyasztóvédelmi szabályoknak való megfelelés</li>
                <li>Kötelező adatszolgáltatások teljesítésének biztonsági aspektusai</li>
                <li>Megfelelési programok és belső ellenőrzések</li>
                <li>Energiapiaci integritás vizsgálati jelentések</li>
                <li>Szabályozói beavatkozásokra vonatkozó válaszképesség bizonyítéka</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Tanácsadói és Ügyfél feladatok */}
      <div ref={nis2TasksRef} className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl shadow-lg p-8 border border-blue-200 mb-12">
        <h2 className="text-2xl font-bold mb-6 text-blue-900">NIS2 Megfelelőségi Feladatok</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Bal oszlop - Szervezeti checklist */}
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Alapvető szervezeti információk
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Szervezet neve, címe, elérhetőségei</li>
                <li>Fő tevékenységi területek és szolgáltatások</li>
                <li>Szervezeti struktúra és felelősségi körök</li>
                <li>Alkalmazottak száma és eloszlása</li>
                <li>IT infrastruktúra áttekintése</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Kockázatkezelési intézkedések
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Kockázatértékelési módszertan és gyakoriság</li>
                <li>Azonosított kockázatok nyilvántartása</li>
                <li>Kockázatkezelési stratégiák és intézkedések</li>
                <li>Kockázati étvágy meghatározása</li>
                <li>Kockázatkezelési jelentések és felülvizsgálatok</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Alapvető biztonsági intézkedések
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Hálózat- és rendszerbiztonság</li>
                <li>Hálózati határvédelem (tűzfalak, IDS/IPS)</li>
                <li>Hozzáférés-kezelés és azonosítás</li>
                <li>Rendszerhardening és konfiguráció-menedzsment</li>
                <li>Végpontvédelem és malware elleni védelem</li>
                <li>Adattitkosítás és kulcskezelés</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Fizikai biztonság
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Fizikai hozzáférés-védelem</li>
                <li>Környezeti védelem (tűz, víz, áram)</li>
                <li>Eszközök és adathordozók kezelése</li>
                <li>Fizikai biztonsági incidensek kezelése</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Ellátási lánc biztonság
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Beszállítók biztonsági értékelése</li>
                <li>Harmadik felek hozzáférés-kezelése</li>
                <li>Szerződéses biztonsági követelmények</li>
                <li>Beszállítói audit és megfelelőség-ellenőrzés</li>
              </ul>
            </div>
          </div>
          
          {/* Jobb oszlop - További checklist elemek */}
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Üzletmenet-folytonosság és incidens-kezelés
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Üzletmenet-folytonossági tervek</li>
                <li>Katasztrófa-helyreállítási tervek</li>
                <li>Incidens-felismerési és -jelentési eljárások</li>
                <li>Incidens-kezelési folyamatok és felelősségek</li>
                <li>Tesztelés és gyakorlatok</li>
                <li>Hatóságok felé történő jelentési kötelezettségek</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Biztonsági szabályzatok és eljárások
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Információbiztonsági szabályzat</li>
                <li>Adatvédelmi szabályzat</li>
                <li>Hozzáférés-kezelési szabályzat</li>
                <li>Változáskezelési eljárások</li>
                <li>Biztonsági tudatossági program</li>
                <li>Megfelelőség-ellenőrzési eljárások</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                NIS2 Technikai Megfeleltetési Útmutató
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Technikai megfelelőségi követelmények</li>
                <li>Biztonsági architektúra tervezése</li>
                <li>Védelmi rétegek kialakítása</li>
                <li>Monitorozási és naplózási rendszerek</li>
                <li>Sérülékenységkezelés és javítás</li>
                <li>Biztonsági tesztelés és értékelés</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Tanácsadói feladatok
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Előzetes felmérés és gap analízis</li>
                <li>Adatgyűjtés és elemzés</li>
                <li>Kockázatértékelés</li>
                <li>Intézkedési terv kidolgozása</li>
                <li>Implementáció támogatása</li>
                <li>Oktatás és tudatosítás</li>
                <li>Megfelelőség ellenőrzése és dokumentálása</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-indigo-800 mb-4 border-b border-purple-300 pb-2">
                Ügyfél feladatai
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Adatszolgáltatás és információ biztosítása</li>
                <li>Erőforrások allokálása</li>
                <li>Belső koordináció</li>
                <li>Javasolt intézkedések implementálása</li>
                <li>Dokumentáció elkészítése és karbantartása</li>
                <li>Folyamatos megfelelőség biztosítása</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* NIS2 Gyakorlati Implementációs Útmutató */}
      <div ref={nis2ImplementationGuideRef} className="bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl shadow-lg p-8 border border-orange-200 mb-12">
        <h2 className="text-2xl font-bold mb-6 text-orange-900">NIS2 Gyakorlati Implementációs Útmutató</h2>
        
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-orange-800 mb-3">Implementációs lépések</h3>
          <div className="relative">
            <div className="absolute left-4 inset-y-0 w-0.5 bg-orange-300"></div>
            <div className="space-y-6 relative">
              <div className="flex">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center relative z-10 mr-4">1</div>
                <div className="bg-white rounded-lg p-4 shadow flex-grow">
                  <h4 className="text-lg font-medium text-orange-700 mb-2">Előkészítés és felmérés</h4>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>NIS2 hatókör meghatározása a szervezetre</li>
                    <li>Jelenlegi állapot felmérése (gap analízis)</li>
                    <li>Érintett rendszerek és szolgáltatások azonosítása</li>
                    <li>Felelősségi körök meghatározása</li>
                  </ul>
                </div>
              </div>
              <div className="flex">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center relative z-10 mr-4">2</div>
                <div className="bg-white rounded-lg p-4 shadow flex-grow">
                  <h4 className="text-lg font-medium text-orange-700 mb-2">Kockázatértékelés</h4>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Kockázatértékelési módszertan kidolgozása</li>
                    <li>Kritikus eszközök és folyamatok azonosítása</li>
                    <li>Fenyegetések és sebezhetőségek elemzése</li>
                    <li>Kockázati szintek meghatározása</li>
                  </ul>
                </div>
              </div>
              <div className="flex">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center relative z-10 mr-4">3</div>
                <div className="bg-white rounded-lg p-4 shadow flex-grow">
                  <h4 className="text-lg font-medium text-orange-700 mb-2">Biztonsági intézkedések implementálása</h4>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Technikai kontrollok bevezetése</li>
                    <li>Szervezeti intézkedések implementálása</li>
                    <li>Folyamatok és eljárások kidolgozása</li>
                    <li>Oktatás és tudatosítás</li>
                  </ul>
                </div>
              </div>
              <div className="flex">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center relative z-10 mr-4">4</div>
                <div className="bg-white rounded-lg p-4 shadow flex-grow">
                  <h4 className="text-lg font-medium text-orange-700 mb-2">Incidenskezelés fejlesztése</h4>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Incidenskezelési folyamatok felülvizsgálata</li>
                    <li>Jelentési eljárások kidolgozása</li>
                    <li>Incidenskezelési gyakorlatok</li>
                    <li>Helyreállítási tervek tesztelése</li>
                  </ul>
                </div>
              </div>
              <div className="flex">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center relative z-10 mr-4">5</div>
                <div className="bg-white rounded-lg p-4 shadow flex-grow">
                  <h4 className="text-lg font-medium text-orange-700 mb-2">Dokumentáció és megfelelőség</h4>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Biztonsági dokumentáció elkészítése</li>
                    <li>Megfelelőségi nyilvántartások vezetése</li>
                    <li>Belső auditok végrehajtása</li>
                    <li>Folyamatos fejlesztés biztosítása</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-xl font-semibold text-orange-800 mb-3">Gyakori kihívások</h3>
            <div className="bg-white rounded-lg p-4 shadow">
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Erőforrás-hiány:</strong> Megfelelő szakértelem és kapacitás biztosítása</li>
                <li><strong>Komplex IT környezet:</strong> Heterogén rendszerek és legacy megoldások kezelése</li>
                <li><strong>Beszállítói lánc:</strong> Harmadik felek megfelelőségének biztosítása</li>
                <li><strong>Folyamatos változás:</strong> Technológiai és szabályozási környezet változásainak követése</li>
                <li><strong>Vezetői elkötelezettség:</strong> Megfelelő támogatás és erőforrások biztosítása</li>
              </ul>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-orange-800 mb-3">Sikertényezők</h3>
            <div className="bg-white rounded-lg p-4 shadow">
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Korai kezdés:</strong> Időben elkezdett felkészülés és tervezés</li>
                <li><strong>Integrált megközelítés:</strong> Meglévő biztonsági keretrendszerek kihasználása</li>
                <li><strong>Kockázatalapú szemlélet:</strong> Erőforrások hatékony allokálása</li>
                <li><strong>Automatizáció:</strong> Megfelelőségi folyamatok és ellenőrzések automatizálása</li>
                <li><strong>Tudatosság:</strong> Szervezeti kultúra fejlesztése</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="text-xl font-semibold text-orange-800 mb-3">Legjobb gyakorlatok</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow flex flex-col">
              <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-orange-700 mb-2">Szabványok alkalmazása</h4>
              <p className="text-sm text-gray-600 flex-grow">ISO 27001, NIST és más szabványok integrálása a NIS2 megfelelőségbe</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow flex flex-col">
              <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-orange-700 mb-2">Multidiszciplináris csapat</h4>
              <p className="text-sm text-gray-600 flex-grow">IT, jogi, üzleti és biztonsági szakértők bevonása a megfelelőségi projektbe</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow flex flex-col">
              <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-orange-700 mb-2">Dokumentáció</h4>
              <p className="text-sm text-gray-600 flex-grow">Részletes és naprakész dokumentáció minden biztonsági intézkedésről és döntésről</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow flex flex-col">
              <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-orange-700 mb-2">Mérés és értékelés</h4>
              <p className="text-sm text-gray-600 flex-grow">Biztonsági intézkedések hatékonyságának rendszeres mérése és értékelése</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow flex flex-col">
              <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2M7 7h10" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-orange-700 mb-2">Technológiai megoldások</h4>
              <p className="text-sm text-gray-600 flex-grow">Megfelelő biztonsági technológiák és eszközök bevezetése a követelmények teljesítéséhez</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow flex flex-col">
              <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-lg font-medium text-orange-700 mb-2">Incidens-szimuláció</h4>
              <p className="text-sm text-gray-600 flex-grow">Rendszeres gyakorlatok és szimulációk az incidenskezelési képességek tesztelésére</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-800 rounded-xl shadow-lg p-8 text-white text-center mb-12">
        <h2 className="text-2xl font-bold mb-4">Kérjen szakértői segítséget a NIS2 megfelelőséghez</h2>
        <p className="mb-6 text-lg">
          Szakértő csapatunk segít a NIS2 követelmények teljesítésében és a megfelelőség fenntartásában. A fenti listák csak kiindulópontot jelentenek, a valós megfelelőség egyedi értékelést igényel.
        </p>
        <a 
          href="/kapcsolat" 
          className="inline-block px-6 py-3 bg-white text-purple-700 font-semibold rounded-lg hover:bg-purple-100 transition-colors duration-200"
        >
          Kapcsolatfelvétel
        </a>
      </div>

      {/* Vissza a tetejére gomb */}
      <button
        onClick={scrollToTop}
        className={`fixed right-6 bottom-6 bg-purple-600 hover:bg-purple-700 text-white rounded-full p-3 shadow-lg transition-all duration-300 flex items-center justify-center ${
          showScrollToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Vissza a tetejére"
      >
        <div className="relative">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          <span className="absolute -bottom-7 left-1/2 transform -translate-x-1/2 text-xs whitespace-nowrap font-medium">
            Vissza fel
          </span>
        </div>
      </button>
    </main>
  );
};

export default Information; 